// src/pages/gate/GateVisitorEntry.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./GateVisitorEntry.css";

export default function GateVisitorEntry() {
  const navigate = useNavigate();
  const gateName = localStorage.getItem("gateName");
  const gateShiftId = localStorage.getItem("gateShiftId");
  const gateToken = localStorage.getItem("gateToken");

  const [formData, setFormData] = useState({
    visitorName: "",
    visitorEmail: "",
    visitorPhone: "",
    visitorType: "guest", // "guest" or "vendor"
    vendorCompany: "",
    vendorCategory: "",
    residentId: "",
    purpose: "",
    vehicleNumber: "",
    expectedDuration: "1 hour",
    isPreApproved: false
  });

  const [residents, setResidents] = useState([]);
  const [allResidents, setAllResidents] = useState([]);
  const [towers, setTowers] = useState([]);
  const [floors, setFloors] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Quick search — find a resident by name/unit/tower in one box instead
  // of three sequential dropdowns. The dropdowns below still work as a
  // fallback (e.g. gate staff who don't know the resident's name yet).
  const [residentSearch, setResidentSearch] = useState("");
  const [showResidentResults, setShowResidentResults] = useState(false);
  const residentSearchRef = useRef(null);

  const residentSearchResults =
    residentSearch.trim().length > 0
      ? allResidents
          .filter((r) => {
            const q = residentSearch.trim().toLowerCase();
            return (
              r.name?.toLowerCase().includes(q) ||
              r.unitNumber?.toLowerCase().includes(q) ||
              String(r.tower || "").toLowerCase().includes(q)
            );
          })
          .slice(0, 8)
      : [];

  useEffect(() => {
    // Redirect if not logged in
    if (!gateToken) {
      navigate("/gate/login");
      return;
    }

    fetchResidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the search-results dropdown on outside click.
  useEffect(() => {
    if (!showResidentResults) return undefined;
    const onClick = (e) => {
      if (residentSearchRef.current && !residentSearchRef.current.contains(e.target)) {
        setShowResidentResults(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showResidentResults]);

  // Keep the search box showing whichever resident is actually selected,
  // however they were picked (search result, or the dropdowns below).
  useEffect(() => {
    if (!formData.residentId) return;
    const picked = allResidents.find((r) => r.id === formData.residentId);
    if (picked) {
      setResidentSearch(`${picked.name} — ${picked.unitNumber || "no unit on file"}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.residentId]);

  const fetchResidents = async () => {
    try {
      setLoading(true);
      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/gate/residents`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${gateToken}`
        }
      });

      if (!response.ok) throw new Error("Failed to fetch residents");
      const data = await response.json();
      const allRes = data.residents || [];

      setAllResidents(allRes);

      // Extract unique towers
      const uniqueTowers = [...new Set(allRes.map(r => r.tower))].sort();
      setTowers(uniqueTowers);

      // Reset filters
      setSelectedTower("");
      setSelectedFloor("");
      setResidents([]);
    } catch (err) {
      setError("Failed to load residents: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTowerChange = (tower) => {
    setSelectedTower(tower);
    setSelectedFloor("");

    // Extract unique floors for selected tower
    const towersResidents = allResidents.filter(r => r.tower === tower);
    const uniqueFloors = [...new Set(towersResidents.map(r => r.floor || "Unknown"))].sort((a, b) => {
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      return parseInt(a) - parseInt(b);
    });
    setFloors(uniqueFloors);
    setResidents([]);
  };

  const handleFloorChange = (floor) => {
    setSelectedFloor(floor);

    // Filter residents by tower and floor
    const filtered = allResidents.filter(r =>
      r.tower === selectedTower && (r.floor === floor || (floor === "Unknown" && !r.floor))
    );
    setResidents(filtered.sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleSelectResidentFromSearch = (resident) => {
    // Drive the same state the Tower/Floor/Resident dropdowns use, so
    // whichever path gate staff picked, the form ends up in the same
    // consistent state (and the resident <select> below has a matching
    // option, since it's populated from `residents`).
    const floorValue = resident.floor || "Unknown";
    const towerResidents = allResidents.filter((r) => r.tower === resident.tower);
    const uniqueFloors = [...new Set(towerResidents.map((r) => r.floor || "Unknown"))].sort((a, b) => {
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      return parseInt(a) - parseInt(b);
    });
    const filtered = towerResidents.filter(
      (r) => r.floor === floorValue || (floorValue === "Unknown" && !r.floor)
    );

    setSelectedTower(resident.tower);
    setFloors(uniqueFloors);
    setSelectedFloor(floorValue);
    setResidents(filtered.sort((a, b) => a.name.localeCompare(b.name)));
    setFormData((prev) => ({ ...prev, residentId: resident.id }));
    setShowResidentResults(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (!formData.visitorName || !formData.residentId) {
        throw new Error("Visitor name and resident are required");
      }

      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/visitors/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${gateToken}`
        },
        body: JSON.stringify({
          ...formData,
          gateStaffName: gateName,
          gateShiftId: gateShiftId
        })
      });

      if (!response.ok) {
        throw new Error("Failed to create visitor entry");
      }

      await response.json();
      setSuccess(`✅ ${formData.visitorName} registered successfully!`);

      // Reset form
      setFormData({
        visitorName: "",
        visitorEmail: "",
        visitorPhone: "",
        visitorType: "guest",
        vendorCompany: "",
        vendorCategory: "",
        residentId: "",
        purpose: "",
        vehicleNumber: "",
        expectedDuration: "1 hour",
        isPreApproved: false
      });
      setResidentSearch("");
      setSelectedTower("");
      setSelectedFloor("");
      setResidents([]);

      setTimeout(() => {
        setSuccess("");
      }, 5000);
    } catch (err) {
      setError("Error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("gateToken");
    localStorage.removeItem("gateName");
    localStorage.removeItem("gateShiftId");
    localStorage.removeItem("gateShiftPin");
    navigate("/gate/login");
  };

  if (!gateToken) {
    return null;
  }

  return (
    <div className="gate-entry-container">
      <div className="gate-entry-header">
        <div className="gate-info">
          <h1>🚪 Main Gate Visitor Entry</h1>
          <p>Gate Staff: {gateName}</p>
        </div>
        <button className="gate-logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      <div className="gate-entry-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {loading ? (
          <div className="loading">Loading residents...</div>
        ) : (
          <form onSubmit={handleSubmit} className="gate-visitor-form">
            <div className="form-section">
              <h2>👤 Visitor Information</h2>

              <div className="form-group">
                <label>Visitor Type</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="visitorType"
                      value="guest"
                      checked={formData.visitorType === "guest"}
                      onChange={handleInputChange}
                    />
                    Guest
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="visitorType"
                      value="vendor"
                      checked={formData.visitorType === "vendor"}
                      onChange={handleInputChange}
                    />
                    Vendor/Service Provider
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="visitorName">Visitor Name *</label>
                <input
                  type="text"
                  id="visitorName"
                  name="visitorName"
                  value={formData.visitorName}
                  onChange={handleInputChange}
                  placeholder="Full name"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="visitorEmail">Email</label>
                  <input
                    type="email"
                    id="visitorEmail"
                    name="visitorEmail"
                    value={formData.visitorEmail}
                    onChange={handleInputChange}
                    placeholder="email@example.com"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="visitorPhone">Phone</label>
                  <input
                    type="tel"
                    id="visitorPhone"
                    name="visitorPhone"
                    value={formData.visitorPhone}
                    onChange={handleInputChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="form-input"
                  />
                </div>
              </div>

              {formData.visitorType === "vendor" && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="vendorCompany">Company Name</label>
                      <input
                        type="text"
                        id="vendorCompany"
                        name="vendorCompany"
                        value={formData.vendorCompany}
                        onChange={handleInputChange}
                        placeholder="Company name"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="vendorCategory">Service Category</label>
                      <select
                        id="vendorCategory"
                        name="vendorCategory"
                        value={formData.vendorCategory}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        <option value="">Select category</option>
                        <option value="plumbing">Plumbing</option>
                        <option value="electrical">Electrical</option>
                        <option value="carpentry">Carpentry</option>
                        <option value="cleaning">Cleaning</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="delivery">Delivery</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="form-section">
              <h2>🏠 Resident Information</h2>

              <div className="form-group resident-search-group" ref={residentSearchRef}>
                <label htmlFor="residentSearch">Find Resident</label>
                <input
                  type="text"
                  id="residentSearch"
                  className="form-input"
                  placeholder="Type a name, unit number, or tower..."
                  value={residentSearch}
                  onChange={(e) => {
                    setResidentSearch(e.target.value);
                    setShowResidentResults(true);
                    if (!e.target.value.trim()) {
                      setFormData((prev) => ({ ...prev, residentId: "" }));
                    }
                  }}
                  onFocus={() => setShowResidentResults(true)}
                  autoComplete="off"
                />
                {showResidentResults && residentSearch.trim() && (
                  <div className="resident-search-results">
                    {residentSearchResults.length === 0 ? (
                      <div className="resident-search-empty">No matching residents</div>
                    ) : (
                      residentSearchResults.map((r) => (
                        <button
                          type="button"
                          key={r.id}
                          className="resident-search-result"
                          onClick={() => handleSelectResidentFromSearch(r)}
                        >
                          <span className="resident-search-name">{r.name}</span>
                          <span className="resident-search-meta">
                            {r.tower}
                            {r.floor ? ` · Floor ${r.floor}` : ""} · {r.unitNumber || "no unit on file"}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                <small className="help-text">
                  Fastest way to find someone — or use the dropdowns below.
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="tower">Select Tower *</label>
                  <select
                    id="tower"
                    value={selectedTower}
                    onChange={(e) => handleTowerChange(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Choose tower</option>
                    {towers.map((tower) => (
                      <option key={tower} value={tower}>
                        {tower}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="floor">Select Floor *</label>
                  <select
                    id="floor"
                    value={selectedFloor}
                    onChange={(e) => handleFloorChange(e.target.value)}
                    className="form-input"
                    disabled={!selectedTower}
                    required
                  >
                    <option value="">Choose floor</option>
                    {floors.map((floor) => (
                      <option key={floor} value={floor}>
                        {floor === "Unknown" ? "Unknown Floor" : `Floor ${floor}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="residentId">Visiting Resident *</label>
                <select
                  id="residentId"
                  name="residentId"
                  value={formData.residentId}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!selectedFloor}
                  required
                >
                  <option value="">Select resident</option>
                  {residents.map((resident) => (
                    <option key={resident.id} value={resident.id}>
                      {resident.name} - {resident.unitNumber}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="purpose">Purpose of Visit</label>
                  <input
                    type="text"
                    id="purpose"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    placeholder="e.g., Repair, Delivery, Social visit"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="expectedDuration">Expected Duration</label>
                  <select
                    id="expectedDuration"
                    name="expectedDuration"
                    value={formData.expectedDuration}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="30 minutes">30 minutes</option>
                    <option value="1 hour">1 hour</option>
                    <option value="2 hours">2 hours</option>
                    <option value="4 hours">4 hours</option>
                    <option value="Full day">Full day</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="vehicleNumber">Vehicle Number</label>
                <input
                  type="text"
                  id="vehicleNumber"
                  name="vehicleNumber"
                  value={formData.vehicleNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., MH 01 AB 1234"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-section">
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="isPreApproved"
                    checked={formData.isPreApproved}
                    onChange={handleInputChange}
                  />
                  Pre-approved by resident
                </label>
                <p className="help-text">
                  Check if resident has already approved this visitor
                </p>
              </div>
            </div>

            <button
              type="submit"
              className="gate-submit-btn"
              disabled={submitting || loading}
            >
              {submitting ? "Registering..." : "✅ Register Visitor"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}